import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { BulletinsState } from 'store/modules/bulletins_store/types';
import {
  markAllBulletinsAsRead,
  fetchBulletins,
  fetchBulletinsRepresented,
} from 'store/modules/bulletins_store/actions';
import { ProfileState } from 'store/modules/profile_store/types';
import { totalBiddingUnread } from 'services/actions/panel_actions';
import tabId from 'services/tabId';
import { MarkAllButton } from './styles';

interface MarkAllBulletinsButtonProps {
  selectedFilterId: number | null;
}

const MarkAllBulletinsButton: React.FC<MarkAllBulletinsButtonProps> = ({ selectedFilterId }) => {
  const dispatch = useDispatch();
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const { bulletins, request } = useSelector<ApplicationState, BulletinsState>(
    (state) => state.session.bulletins,
  );

  const [showModal, setShowModal] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const companyId = profile?.currentCompany?.[tabId()] || profile?.clientId;
  const loading = request.mark_all_bulletins_read?.loading;

  const selectedFilterName = bulletins?.filter_bulletins?.find(
    (fb) => fb?.filter?.id === selectedFilterId,
  )?.filter?.description;

  const refreshUnreadCount = async () => {
    if (!companyId || !selectedFilterId) {
      setUnreadCount(0);
      return;
    }
    const total = await totalBiddingUnread(companyId, selectedFilterId);
    setUnreadCount(total);
  };

  useEffect(() => {
    refreshUnreadCount();
  }, [companyId, selectedFilterId]);

  const handleConfirm = async () => {
    if (!companyId || !selectedFilterId || loading) return;
    setErrorMessage(null);
    try {
      await dispatch(markAllBulletinsAsRead(companyId, selectedFilterId) as any);
      if (companyId === profile?.clientId) {
        dispatch(fetchBulletins());
      } else {
        dispatch(fetchBulletinsRepresented(companyId));
      }
      await refreshUnreadCount();
      setShowModal(false);
    } catch (error: any) {
      const apiMessage = error?.response?.data?.error;
      setErrorMessage(
        apiMessage || 'Não foi possível marcar os boletins como lidos. Tente novamente.',
      );
    }
  };

  const handleClose = () => {
    if (loading) return;
    setErrorMessage(null);
    setShowModal(false);
  };

  const isDisabled = !companyId || !selectedFilterId || unreadCount === 0 || loading;

  return (
    <>
      <MarkAllButton
        type="button"
        className="btn btn-sm d-flex align-items-center"
        disabled={isDisabled}
        onClick={() => setShowModal(true)}
      >
        <span className="material-icons mr-1" style={{ fontSize: '18px' }}>
          done_all
        </span>
        Marcar boletins do filtro como lido
        {unreadCount > 0 && <span className="badge mark-all-badge ml-2">{unreadCount}</span>}
      </MarkAllButton>

      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header>
          <Modal.Title>Marcar boletins como lidos</Modal.Title>
          <button type="button" className="close" disabled={loading} onClick={handleClose}>
            <span aria-hidden="true">&times;</span>
          </button>
        </Modal.Header>
        <Modal.Body>
          Marcar os <strong>{unreadCount}</strong> boletins não visualizados do filtro{' '}
          <strong>{selectedFilterName}</strong> como lidos?
          {errorMessage && (
            <div className="alert alert-danger mt-3 mb-0" role="alert">
              {errorMessage}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={loading}
            onClick={handleClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={loading}
            onClick={handleConfirm}
          >
            {loading ? 'Marcando...' : 'Confirmar'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MarkAllBulletinsButton;
